import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { ArrowRight, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CategoryBadge } from './CategoryBadge';
import type { Product } from '../../../../shared/types/partnerships';

interface ProductCardProps {
  product: Product;
  partnerSlug: string;
  canEdit?: boolean;
  onEdit?: (product: Product) => void;
}

export function ProductCard({ product, partnerSlug, canEdit, onEdit }: ProductCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="group relative flex flex-col hover:shadow-md transition-shadow">
      <div className="flex-1 p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <CategoryBadge category={product.category} />
          {canEdit && onEdit && (
            <button
              onClick={() => onEdit(product)}
              className="opacity-0 group-hover:opacity-100 text-xs text-muted-foreground hover:text-foreground transition-opacity"
            >
              {t('common.ui.edit')}
            </button>
          )}
        </div>

        <div>
          <h3 className="font-semibold leading-tight">{product.name}</h3>
          {product.tagline && (
            <p className="text-sm text-muted-foreground mt-1 leading-snug">{product.tagline}</p>
          )}
        </div>

        {product.availableMarkets.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Globe className="h-3 w-3 shrink-0" />
            {product.availableMarkets.slice(0, 3).join(', ')}
            {product.availableMarkets.length > 3 && ` +${product.availableMarkets.length - 3}`}
          </div>
        )}
      </div>

      <div className="px-5 pb-4">
        <Link
          to={`/partnerships/${partnerSlug}/products/${product.productId}`}
          className="inline-flex items-center gap-1 text-sm font-medium hover:gap-2 transition-all text-primary"
        >
          {t('partnerships.common.view_detail')} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </Card>
  );
}
